import React, { useState } from "react";
import { ArrowLeftRight, Plus, Truck, CheckCircle2, Clock, Check } from "lucide-react";
import { useApp } from "../../app/context/AppContext";
import { DataTable } from "../../components/common/DataTable";
import { Badge } from "../../components/common/Badge";
import { formatDate } from "../../utils/formatters";
import { TransferModal } from "./TransferModal";
import { useNotification } from "../../app/context/NotificationContext";

export function StockTransferModule() {
  const { stockTransfers, completeStockTransfer } = useApp();
  const { addToast } = useNotification();
  const [isModalOpen, setIsModalOpen] = useState(false);

  const columns = [
    {
      header: "Transfer Code",
      accessorKey: "transferCode",
      render: (val, row) => (
        <div>
          <span className="font-bold font-mono text-slate-900 dark:text-white">{val}</span>
          <p className="text-[10px] text-slate-400 font-mono">Date: {formatDate(row.date)}</p>
        </div>
      )
    },
    {
      header: "Route (From → To)",
      accessorKey: "fromLocationName",
      render: (_, row) => (
        <div className="flex items-center gap-1.5 font-medium text-slate-800 dark:text-slate-200">
          <span>{row.fromLocationName?.split("(")[0]}</span>
          <ArrowLeftRight className="w-3 h-3 text-emerald-500" />
          <span>{row.toLocationName?.split("(")[0]}</span>
        </div>
      )
    },
    {
      header: "Transfer Type",
      accessorKey: "type",
      render: (val) => <Badge variant="blue">{val}</Badge>
    },
    {
      header: "Units Transferred",
      accessorKey: "totalUnits",
      align: "center",
      render: (val, row) => (
        <span className="font-mono font-bold text-slate-900 dark:text-white">
          {val} units ({row.productCount} SKUs)
        </span>
      )
    },
    {
      header: "Status Pipeline",
      accessorKey: "status",
      render: (status) => {
        const map = {
          Pending: { variant: "default", text: "Pending Dispatch" },
          "In Transit": { variant: "amber", text: "In Transit 🚚" },
          Completed: { variant: "emerald", text: "Delivered & Received" }
        };
        const st = map[status] || map.Pending;
        return <Badge variant={st.variant}>{st.text}</Badge>;
      }
    },
    {
      header: "Responsible & Driver",
      accessorKey: "responsiblePerson",
      render: (val, row) => (
        <div>
          <p className="text-slate-700 dark:text-slate-300 font-medium">{val}</p>
          <p className="text-[10px] text-slate-400">{row.driverName}</p>
        </div>
      )
    },
    {
      header: "Actions",
      accessorKey: "actions",
      sortable: false,
      render: (_, row) => (
        <div className="text-right">
          {row.status === "In Transit" && (
            <button
              onClick={async (e) => {
                e.stopPropagation();
                await completeStockTransfer(row.id);
                addToast(`Transfer ${row.transferCode} received and stocked at destination!`, "success");
              }}
              className="inline-flex items-center gap-1 px-2.5 py-1 bg-emerald-600 hover:bg-emerald-500 text-white rounded-lg font-semibold text-[10px] shadow"
            >
              <Check className="w-3 h-3" />
              Mark Received
            </button>
          )}
        </div>
      )
    }
  ];

  return (
    <div className="space-y-6">
      {/* Top Banner */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h2 className="text-xl font-bold text-slate-900 dark:text-white font-display flex items-center gap-2">
            <ArrowLeftRight className="w-5 h-5 text-emerald-500" />
            Stock Transfer Logistics & Fleet Tracker
          </h2>
          <p className="text-xs text-slate-500">
            Shuttle inventory between Central Kattedan Mega Hub, Begum Bazar Mandi Depot, and Kukatpally Retail Godown
          </p>
        </div>

        <button
          onClick={() => setIsModalOpen(true)}
          className="inline-flex items-center gap-1.5 px-4 py-2 bg-emerald-600 hover:bg-emerald-500 text-white text-xs font-semibold rounded-xl shadow-lg shadow-emerald-600/30 transition"
        >
          <Plus className="w-4 h-4" />
          Create Stock Transfer
        </button>
      </div>

      <DataTable
        columns={columns}
        data={stockTransfers}
        title="Inter-Warehouse Transfer Manifests"
        subtitle="Live tracking of moving batches, drivers, and receipt authorizations"
        filename="kirana_stock_transfers"
      />

      <TransferModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
      />
    </div>
  );
}
